import { useCallback, useSyncExternalStore } from 'react';

/**
 * Theme preference.
 *
 * Three states rather than two: `light`, `dark`, and `system`. A two-state
 * toggle has to pick a side on first visit, and whichever it picks is wrong for
 * somebody — most people leave the OS setting on a schedule (light by day, dark
 * by night) and a console that ignores it is a console that blinds them at 2am.
 * `system` is the default, and it keeps following the OS.
 *
 * ## Why `useSyncExternalStore` rather than context
 *
 * The theme is a value that lives outside React — it is a class on `<html>` and a
 * key in `localStorage`, both of which exist before React mounts and both of
 * which the inline script in `index.html` has already touched. Modelling it as
 * React state would mean the first render disagrees with the DOM, which is the
 * flash of wrong theme the inline script exists to prevent.
 *
 * `useSyncExternalStore` is the supported way to read an external mutable source
 * without tearing: it subscribes, re-reads the snapshot, and guarantees every
 * component in a render pass sees the same value.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'keystone.theme';
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

const listeners = new Set<() => void>();
let mediaQuery: MediaQueryList | null = null;

/** True when the reader has asked the OS to reduce motion. */
export function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Reads the stored preference.
 *
 * `localStorage` throws rather than returning null when storage is blocked — in
 * Safari's private mode, and under any number of privacy extensions. A theme
 * preference is not worth an exception, so every access is guarded and the
 * fallback is the default.
 */
function readPreference(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Storage unavailable. Fall through to the default.
  }
  return 'system';
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MEDIA_QUERY).matches;
}

function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

/**
 * Applies a theme to the document.
 *
 * Only the `dark` class is toggled; the light theme is the absence of it. That
 * matches the CSS, where `:root` holds the light tokens and `.dark` overrides
 * them, and it means a browser with JavaScript disabled gets a working light
 * theme rather than an unstyled page.
 */
function apply(theme: ResolvedTheme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

function emit(): void {
  for (const listener of listeners) listener();
}

/* -------------------------------------------------------------------------- */
/*  Public surface                                                            */
/* -------------------------------------------------------------------------- */

export function getPreference(): ThemePreference {
  return readPreference();
}

export function getResolvedTheme(): ResolvedTheme {
  return resolve(readPreference());
}

export function setPreference(preference: ThemePreference): void {
  try {
    // `system` is stored rather than deleted. Deleting it would make "follow the
    // OS" indistinguishable from "never chose", which is fine today but would
    // break the moment a first-run flow wanted to show a picker only once.
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Storage unavailable: the preference applies for this page view and is
    // forgotten on reload. Acceptable, and better than throwing.
  }
  apply(resolve(preference));
  emit();
}

/** Cycles light -> dark -> system, for a single-button toggle. */
export function cyclePreference(): ThemePreference {
  const order: ThemePreference[] = ['light', 'dark', 'system'];
  const current = readPreference();
  const next = order[(order.indexOf(current) + 1) % order.length] ?? 'system';
  setPreference(next);
  return next;
}

/**
 * Subscribes to changes from any source: this tab, another tab, or the OS.
 *
 * The `storage` event is what keeps two open tabs in agreement. The media query
 * listener is attached once, on the first subscriber, and only matters while the
 * preference is `system` — but it is cheaper to leave it attached than to add
 * and remove it on every preference change, and the handler is a no-op when the
 * preference is explicit.
 */
function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  if (listeners.size === 1) {
    mediaQuery ??= window.matchMedia(MEDIA_QUERY);
    mediaQuery.addEventListener('change', handleSystemChange);
    window.addEventListener('storage', handleStorage);
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      mediaQuery?.removeEventListener('change', handleSystemChange);
      window.removeEventListener('storage', handleStorage);
    }
  };
}

function handleSystemChange(): void {
  if (readPreference() !== 'system') return;
  apply(resolve('system'));
  emit();
}

function handleStorage(event: StorageEvent): void {
  if (event.key !== null && event.key !== STORAGE_KEY) return;
  apply(resolve(readPreference()));
  emit();
}

/** The current preference, re-rendering the caller when it changes. */
export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, getPreference, () => 'system' as const);
}

/** The theme actually in effect, which is what a preview needs to know. */
export function useResolvedTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribe, getResolvedTheme, () => 'light' as const);
}

/** A stable setter, for a component that offers an explicit choice. */
export function useSetTheme(): (preference: ThemePreference) => void {
  return useCallback((preference: ThemePreference) => setPreference(preference), []);
}
